import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { BlockStatisticsWithCopy, InfoCard, Spinner, StatisticItem } from 'ui';
import { goodFormatNumber } from 'ui/utils';
import { PortfolioExtended } from 'api';

interface PortfolioOverviewStatisticsProps {
  item: PortfolioExtended;
  isFetching?: boolean;
}

const PortfolioOverviewStatistics = ({
  item,
  isFetching = false,
}: PortfolioOverviewStatisticsProps) => {
  const { registerCurrency = 'USD' } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const items = useMemo<StatisticItem[]>(() => {
    const cashOnBanks = item?.bank_actual_cash ?? 0;
    const cashOnBrokers = item?.broker_actual_cash ?? 0;
    const reservedCash = (item?.bank_reserved_cash ?? 0) + (item?.broker_reserved_cash ?? 0);

    return [
      {
        name: 'Portfolio value',
        value: goodFormatNumber(item?.portfolio_value || 0),
        currency: registerCurrency,
      },
      {
        name: 'Cash on Banks',
        value: goodFormatNumber(cashOnBanks),
        currency: registerCurrency,
      },
      {
        name: 'Cash on Brokers',
        value: goodFormatNumber(cashOnBrokers),
        currency: registerCurrency,
      },
      {
        name: 'Reserved Cash',
        value: goodFormatNumber(reservedCash),
        currency: registerCurrency,
      },
      {
        name: 'PNL',
        value: goodFormatNumber(item?.pnl_value || 0),
        currency: registerCurrency,
        className:
          (item?.pnl_value || 0) > 0
            ? 'text-[#16A34A]'
            : (item?.pnl_value || 0) < 0
            ? 'text-[#DC2626]'
            : 'text-[#24699F]',
      },
    ];
  }, [item, registerCurrency]);

  return <BlockStatisticsWithCopy items={items} withBorder={false} isFetching={isFetching} />;
};

export const BlockPortfolioOverviewStatistics = ({
  item,
  isFetching,
}: PortfolioOverviewStatisticsProps) => {
  return (
    <InfoCard
      title="Portfolio balances"
      classes={{ content: 'w-full p-0 px-2 pt-2' }}
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && <Spinner size="md" />}
        </div>
      }
    >
      <PortfolioOverviewStatistics item={item} isFetching={isFetching} />
    </InfoCard>
  );
};
